import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Star,
  MapPin,
  Briefcase,
  Languages,
  IndianRupee,
  MessageCircle,
  Share2,
  Copy,
  Check,
  User,
  Phone,
  Mail,
  Send,
  Scale,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { fetchLawyer, contactLawyer } from '../lib/api';

const initials = (name = '') =>
  name
    .replace(/^Adv\.\s*/i, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('');

const LawyerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lawyer, setLawyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', note: '' });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLawyer(id)
      .then((data) => {
        if (!cancelled) setLawyer(data);
      })
      .catch(() => {
        if (!cancelled) setLawyer(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (lawyer?.name) {
      document.title = `${lawyer.name} — Verified advocate on LawForLayman`;
    } else {
      document.title = 'Advocate profile — LawForLayman';
    }
  }, [lawyer]);

  const handleShareCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success('Profile link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed. Please copy from the URL bar.');
    }
  };

  const handleShareNative = async () => {
    if (!navigator.share) {
      handleShareCopy();
      return;
    }
    try {
      await navigator.share({
        title: `${lawyer.name} on LawForLayman`,
        text: `${lawyer.name} — verified ${lawyer.specialty} advocate on LawForLayman.`,
        url: window.location.href,
      });
    } catch {
      // user cancelled — no-op
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.phone.trim() && !form.email.trim())) {
      toast.error('Please share your name and either a phone number or email.');
      return;
    }
    setSubmitting(true);
    try {
      await contactLawyer({
        lawyer_id: lawyer.id,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        note: form.note.trim(),
      });
      toast.success('Request sent. Our team will introduce you within 24 hours.');
      setForm({ name: '', phone: '', email: '', note: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not send request. Please retry.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-[#5a5a5a]" data-testid="lawyer-profile-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!lawyer) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="lawyer-profile-not-found">
        <h2 className="font-serif-besley text-3xl text-[#0F1B2D] mb-3">Advocate not found</h2>
        <p className="text-[#5a5a5a] mb-6">
          This advocate&apos;s profile may have been removed, or the link is out of date.
        </p>
        <Link to="/find-a-lawyer" className="btn-orange inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to directory
        </Link>
      </div>
    );
  }

  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(
    `${lawyer.name} — verified ${lawyer.specialty} advocate on LawForLayman. ${window.location.href}`,
  )}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`;

  return (
    <div data-testid="lawyer-profile-page">
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-[#C9BFA8] hover:text-[#F8F2E4] inline-flex items-center gap-2 mb-8 transition-colors"
            data-testid="profile-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-6 gap-8 items-start">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-5 mb-6">
                <div className="w-20 h-20 rounded-full bg-[#F8F2E4] text-[#0F1B2D] flex items-center justify-center font-serif-besley text-3xl shrink-0" data-testid="profile-avatar">
                  {initials(lawyer.name)}
                </div>
                <div>
                  <p className="text-xs tracking-[0.25em] text-[#E89530] mb-1.5">VERIFIED ADVOCATE</p>
                  <h1 className="font-serif-besley text-3xl md:text-5xl leading-tight" data-testid="profile-name">
                    {lawyer.name}
                  </h1>
                  <p className="text-[#C9BFA8] mt-1.5" data-testid="profile-specialty">{lawyer.specialty}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 mb-6">
                <Badge className="bg-[#E89530]/15 text-[#E89530] border-[#E89530]/30" data-testid="profile-rating">
                  <Star className="w-3.5 h-3.5 fill-[#E89530] text-[#E89530] mr-1" />
                  {lawyer.rating}
                </Badge>
                <Badge variant="outline" className="border-[#C9BFA8]/40 text-[#C9BFA8]" data-testid="profile-city">
                  <MapPin className="w-3.5 h-3.5 mr-1" /> {lawyer.city}
                </Badge>
                <Badge variant="outline" className="border-[#C9BFA8]/40 text-[#C9BFA8]" data-testid="profile-experience">
                  <Briefcase className="w-3.5 h-3.5 mr-1" /> {lawyer.experience}
                </Badge>
                <Badge variant="outline" className="border-[#C9BFA8]/40 text-[#C9BFA8]" data-testid="profile-fee">
                  <IndianRupee className="w-3.5 h-3.5 mr-0.5" /> {lawyer.fee}
                </Badge>
              </div>

              {lawyer.languages?.length ? (
                <p className="text-sm text-[#C9BFA8] inline-flex items-center gap-2 mb-8">
                  <Languages className="w-4 h-4 text-[#E89530]" />
                  Practises in {lawyer.languages.join(', ')}
                </p>
              ) : null}
            </div>

            <aside className="lg:col-span-2">
              <div className="bg-[#F8F2E4] text-[#0F1B2D] rounded-2xl p-5" data-testid="profile-share-card">
                <p className="text-xs tracking-[0.25em] text-[#A36B1F] mb-3">SHARE PROFILE</p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleShareNative}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-[#0F1B2D] text-[#F8F2E4] hover:bg-[#1a2b45] transition-colors text-sm"
                    data-testid="share-native-btn"
                  >
                    <Share2 className="w-3.5 h-3.5" /> Share
                  </button>
                  <button
                    onClick={handleShareCopy}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[#D6CAB0] hover:bg-[#EDE3CC] transition-colors text-sm"
                    data-testid="share-copy-btn"
                  >
                    {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy link</>}
                  </button>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[#D6CAB0] hover:bg-[#EDE3CC] transition-colors text-sm"
                    data-testid="share-whatsapp-btn"
                  >
                    WhatsApp
                  </a>
                  <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-[#D6CAB0] hover:bg-[#EDE3CC] transition-colors text-sm"
                    data-testid="share-linkedin-btn"
                  >
                    LinkedIn
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-14">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <p className="text-xs tracking-[0.3em] text-[#A36B1F] mb-3">ABOUT</p>
            <h2 className="font-serif-besley text-3xl text-[#0F1B2D] mb-5">Meet {lawyer.name.split(' ').slice(-1)[0]}</h2>
            <p className="text-[15px] text-[#3a3a3a] leading-relaxed whitespace-pre-wrap" data-testid="profile-bio">
              {lawyer.bio || 'Bio not yet added.'}
            </p>

            <div className="mt-8 bg-white border border-[#E8DCC0] rounded-2xl p-6">
              <p className="text-xs tracking-[0.3em] text-[#A36B1F] mb-2">HOW INTRODUCTIONS WORK</p>
              <ol className="space-y-2.5 text-sm text-[#3a3a3a]">
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#0F1B2D] text-[#F8F2E4] flex items-center justify-center text-xs shrink-0">1</span> Submit the introduction request on this page.</li>
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#0F1B2D] text-[#F8F2E4] flex items-center justify-center text-xs shrink-0">2</span> Our coordinator team reviews your query for the right fit.</li>
                <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#0F1B2D] text-[#F8F2E4] flex items-center justify-center text-xs shrink-0">3</span> You&apos;re introduced within 24 working hours. Contact details are never shared without your consent.</li>
              </ol>
            </div>
          </div>

          <aside className="lg:col-span-2">
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-[#E8DCC0] rounded-2xl p-6 sticky top-6"
              data-testid="profile-contact-form"
            >
              <h3 className="font-serif-besley text-2xl text-[#0F1B2D] mb-1.5">Request an introduction</h3>
              <p className="text-sm text-[#5a5a5a] mb-5">
                Our team will pass your query to {lawyer.name.split(' ').slice(-1)[0]}.
              </p>

              <div className="space-y-4">
                <div>
                  <Label className="text-[#0F1B2D]">Your name *</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
                    <Input
                      data-testid="profile-form-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="pl-10 h-11 border-[#E8DCC0] bg-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Phone</Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
                    <Input
                      data-testid="profile-form-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="pl-10 h-11 border-[#E8DCC0] bg-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Email</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
                    <Input
                      data-testid="profile-form-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="pl-10 h-11 border-[#E8DCC0] bg-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-[#0F1B2D]">Short note</Label>
                  <Textarea
                    data-testid="profile-form-note"
                    rows={3}
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className="mt-1.5 border-[#E8DCC0] bg-white"
                    placeholder="A one-line summary of your matter helps us route it correctly."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="profile-form-submit"
                className="btn-orange w-full mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium disabled:opacity-70"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Request introduction
              </button>

              <p className="text-[11px] text-[#5a5a5a] mt-3 text-center flex items-center justify-center gap-1.5">
                <Scale className="w-3 h-3" /> Your details go directly to LawForLayman&apos;s coordinator team.
              </p>
            </form>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default LawyerProfile;
